import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, BookOpen } from 'lucide-react';
import SystemHeader from '../SystemHeader';
import { markdownToHtml } from '../../utils/markdownToHtml';
import MonthYearFilter from './month-year-filter';
import { taiHuongDan } from '../../utils/huongDan';
import ImageModal from './image-modal';

// ─── Color palette for summary cards ────────────────────
const SUMMARY_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
  { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
  { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' },
];

// Loading skeleton for table rows
const TableRowSkeleton = () => (
  <tr className="animate-pulse">
    <td colSpan="99" className="px-3 py-3">
      <div className="h-4 bg-gray-200 rounded w-full" />
    </td>
  </tr>
);

// Loading skeleton for card list
const CardSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
    <div className="h-3 bg-gray-200 rounded w-2/3" />
  </div>
);

// Format helpers for table display
const formatCellValue = (value, col) => {
  if (col.render) return col.render(value, {});
  if (col.format === 'shortDate' || col.key === 'date') {
    if (typeof value === 'string' && value.includes('-')) {
      const parts = value.split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
    }
    return value ?? '—';
  }
  if (col.format === 'currency') {
    const n = parseInt(value);
    if (!n && n !== 0) return '—';
    return n.toLocaleString('vi-VN') + 'đ';
  }
  if (col.format === 'status') {
    const labels = { approved: 'Đã duyệt', pending: 'Chờ duyệt', retail: 'Bán lẻ', wholesale: 'Bán sỉ' };
    return labels[value] || value || '—';
  }
  return value ?? '—';
};

// Compact table view: 1 row per record with alternating colors
const TableView = ({ items, columns, onItemClick, accentColor }) => {
  const visibleCols = columns.filter(c => c.visible !== false);

  // Assign fixed widths (px) to compact columns, rest gets auto (flex)
  const getColStyle = (col) => {
    switch (col.format) {
      case 'shortDate': return { width: '48px', minWidth: '48px' };
      case 'status':    return { width: '100px', minWidth: '100px' };
      case 'image':     return { width: '40px', minWidth: '40px' };
      case 'number':    return { width: '56px', minWidth: '56px' };
      default:          return {}; // auto width, will truncate
    }
  };

  const fixedFormats = ['status', 'shortDate', 'image', 'number'];

  return (
    <div className="w-full overflow-x-auto overflow-y-hidden">
      <table className="w-full text-xs table-fixed min-w-[320px]">
        <colgroup>
          {visibleCols.map((col) => {
            const isFixed = fixedFormats.includes(col.format);
            const style = getColStyle(col);
            return <col key={col.key} style={isFixed ? style : {}} />;
          })}
        </colgroup>
        <thead>
          <tr className="border-b-2" style={{ borderColor: accentColor || '#e5e7eb', backgroundColor: accentColor ? `${accentColor}10` : '#f9fafb' }}>
            {visibleCols.map((col) => (
              <th key={col.key} className="px-1.5 py-2 text-left font-semibold text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr
              key={item.id || index}
              className={`border-b border-gray-100 ${index % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'} ${onItemClick ? 'cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-colors' : ''}`}
              onClick={() => onItemClick && onItemClick(item)}
            >
              {visibleCols.map((col) => {
                const isFixed = fixedFormats.includes(col.format);
                return (
                  <td key={col.key}
                    className={`px-1.5 py-2.5 text-gray-800 ${isFixed ? 'whitespace-nowrap' : 'overflow-hidden text-ellipsis whitespace-nowrap'}`}
                  >
                    {col.render ? col.render(item[col.key], item) : formatCellValue(item[col.key], col)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Default card renderer
const DefaultCard = ({ item, columns, onClick }) => (
  <div
    className={`bg-white rounded-lg border border-gray-200 p-4 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    onClick={() => onClick && onClick(item)}
  >
    {columns.filter(c => c.visible !== false).map((col) => (
      <div key={col.key} className="flex justify-between items-start mb-1 last:mb-0">
        <span className="text-xs text-gray-500 flex-shrink-0 mr-2">{col.label}:</span>
        <span className="text-sm text-gray-900 text-right">
          {col.render ? col.render(item[col.key], item) : (item[col.key] ?? '—')}
        </span>
      </div>
    ))}
  </div>
);

// Compact summary bar — colored cards
const SummaryBar = ({ summary, summaryFields, accentColor }) => {
  if (!summary || !summaryFields || summaryFields.length === 0) return null;
  const cols = summaryFields.length <= 4 ? `grid-cols-${summaryFields.length}` : 'grid-cols-4';
  return (
    <div className={`grid ${cols} gap-1.5 px-3 py-2 border-b border-gray-200`}
         style={{ backgroundColor: accentColor ? `${accentColor}08` : '#f9fafb' }}>
      {summaryFields.map((field, i) => {
        const colorSet = SUMMARY_COLORS[i % SUMMARY_COLORS.length];
        return (
          <div key={field.key} className={`text-center py-1.5 px-1 rounded-lg border ${colorSet.bg} ${colorSet.border}`}>
            <p className={`text-lg font-bold leading-tight ${colorSet.text}`}>
              {field.format ? field.format(summary[field.key]) : (summary[field.key] ?? '—')}
            </p>
            <p className="text-[10px] text-gray-500 leading-tight mt-0.5 truncate">{field.label}</p>
          </div>
        );
      })}
    </div>
  );
};

// Notice block (yellow, markdown-ish)
const NoticeBlock = ({ notice }) => {
  if (!notice || !notice.enabled || !notice.content) return null;
  return (
    <div className="mx-3 mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex gap-2 items-start">
      <span className="text-yellow-600 flex-shrink-0 mt-0.5 text-base">ℹ️</span>
      <div className="text-xs text-yellow-800 leading-relaxed whitespace-pre-wrap">
        {notice.content}
      </div>
    </div>
  );
};


/**
 * GenericFeatureList - Config-driven list page for all dashboard features.
 */
const GenericFeatureList = ({
  title,
  fetchData,
  columns = [],
  summaryFields = [],
  onItemClick,
  createPath,
  renderCard,
  displayMode = 'table',
  emptyMessage = 'Không có dữ liệu cho kỳ này.',
  backPath = '/dashboard',
  notice,
  guideUrl,
  accentColor,
  userInfo,
  accessToken,
  onLogout,
  totalPoints,
  onRefreshPoints,
  actionButton,   // Custom action button component to replace summary + "Thêm mới"
  totalRow,        // { label, columns: { key: value } } - colored total row at bottom
  hideMonthFilter, // Hide month/year filter (e.g. for F1 list)
}) => {
  const navigate = useNavigate();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalImageUrl, setModalImageUrl] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [guideContent, setGuideContent] = useState('');

  // Register global image popup handler for image-format columns
  useEffect(() => {
    window.__showImagePopup = (url) => setModalImageUrl(url);
    return () => { delete window.__showImagePopup; };
  }, []);

  const loadData = useCallback(async () => {
    if (!accessToken) return;
    if (!hideMonthFilter && (!selectedMonth || !selectedYear)) return;
    setLoading(true);
    setError('');
    try {
      const result = hideMonthFilter
        ? await fetchData(accessToken, {})
        : await fetchData(accessToken, { month: selectedMonth, year: selectedYear });
      setItems(result.items || []);
      setSummary(result.summary || {});
    } catch (err) {
      setError('Có lỗi xảy ra khi tải dữ liệu: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, selectedMonth, selectedYear, fetchData, hideMonthFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle={title} onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        {/* Back button row */}
        <div className="flex items-center gap-2 px-3 pt-2 pb-1 bg-white">
          <button onClick={() => navigate(backPath)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors flex-shrink-0"
            style={{ color: accentColor || '#1E5A35', background: (accentColor || '#1E5A35') + '12' }}>
            <ArrowLeft size={14} />
            <span>Quay lại</span>
          </button>
          {guideUrl && (
            <button onClick={async () => {
                setShowGuide(true);
                if (!guideContent) setGuideContent(await taiHuongDan(guideUrl.split('/').pop()));
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ml-auto"
              style={{ color: '#f59e0b', background: '#fef3c7' }}>
              <BookOpen size={12} />
              <span>Hướng dẫn</span>
            </button>
          )}
        </div>
        {/* Title + Month/Year filter on ONE line */}
        <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
          <h1 className="text-sm font-bold flex-shrink-0 whitespace-nowrap" style={{ color: accentColor || undefined }}>
            {title}
          </h1>
          {!hideMonthFilter && (
            <div className="flex-1 min-w-0">
              <MonthYearFilter
                selectedMonth={selectedMonth} selectedYear={selectedYear}
                onMonthChange={(m) => { setSelectedMonth(m); setError(''); }}
                onYearChange={(y) => { setSelectedYear(y); setError(''); }}
              />
            </div>
          )}
        </div>

        {/* Notice block */}
        <NoticeBlock notice={notice} />

        {/* Action button OR Summary bar */}
        {actionButton ? (
          <div className="mx-3 mt-2">{typeof actionButton === 'function' ? actionButton(items, summary) : actionButton}</div>
        ) : (
          <SummaryBar summary={summary} summaryFields={summaryFields} accentColor={accentColor} />
        )}

        {/* Error */}
        {error && (
          <div className="mx-3 mt-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">{error}</div>
        )}

        {/* Records */}
        <div className="flex-1 overflow-y-auto pb-24">
          {loading ? (
            displayMode === 'table' ? (
              <table className="w-full"><tbody>{Array.from({ length: 4 }).map((_, i) => <TableRowSkeleton key={i} />)}</tbody></table>
            ) : (
              <div className="px-3 py-3 space-y-3">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}</div>
            )
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">{emptyMessage}</div>
          ) : displayMode === 'custom' && typeof renderCard === 'function' ? (
            renderCard(items)
          ) : displayMode === 'table' ? (
            <>
              <TableView items={items} columns={columns} onItemClick={onItemClick} accentColor={accentColor} />
              {totalRow && items.length > 0 && (() => {
                const row = typeof totalRow === 'function' ? totalRow(items) : totalRow;
                if (!row) return null;
                return (
                  <div className="mx-3 mb-2 rounded-xl px-4 py-2.5 flex items-center flex-wrap gap-2" style={{ background: (accentColor || '#1E5A35') + '15', border: '1.5px solid ' + (accentColor || '#1E5A35') + '35' }}>
                    <span className="text-xs font-bold flex-1" style={{ color: accentColor || '#1E5A35' }}>{row.label || 'Tổng cộng'}</span>
                    {row.values && Object.entries(row.values).map(([k, v]) => (
                      <span key={k} className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ color: accentColor || '#1E5A35', background: (accentColor || '#1E5A35') + '12' }}>{v}</span>
                    ))}
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="px-3 py-3 space-y-3">
              {items.map((item, index) =>
                renderCard ? renderCard(item, index) : <DefaultCard key={item.id || index} item={item} columns={columns} onClick={onItemClick} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Centered "Thêm mới" button - only if no actionButton */}
      {!actionButton && createPath && (
        <div className="fixed bottom-0 left-0 right-0 flex justify-center pb-5 pt-3 bg-gradient-to-t from-gray-50 via-gray-50/95 to-transparent pointer-events-none z-40">
          <button
            onClick={() => navigate(createPath)}
            className="pointer-events-auto flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm shadow-lg hover:shadow-xl active:scale-95 transition-all"
            style={{ backgroundColor: accentColor || '#1E5A35' }}
          >
            <Plus size={20} />
            <span>Thêm mới</span>
          </button>
        </div>
      )}

      <ImageModal imageUrl={modalImageUrl} onClose={() => setModalImageUrl(null)} />

      {/* Guide popup with animation */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowGuide(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-800">📖 Hướng dẫn</h3>
              <button onClick={() => setShowGuide(false)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"><X size={14} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 text-[13px] text-gray-700 leading-normal" dangerouslySetInnerHTML={{ __html: guideContent ? markdownToHtml(guideContent) : 'Đang tải...' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericFeatureList;
